import { useState } from "react";
import { Feather, ChevronDown, ChevronUp, Link2 } from "lucide-react";

function daysAgoLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "Hoy";
  if (diff === 1) return "Hace 1 día";
  return `Hace ${diff} días`;
}

function shortDate(dateStr) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
  });
}

const PREVIEW_CHARS = 110;

export default function ReflectionsList({ items = [] }) {
  if (!items.length) {
    return (
      <div className="tk-soft-card text-center" data-testid="reflections-empty">
        <p className="font-outfit text-lg text-ink-900">
          Todavía no escribiste ninguna reflexión.
        </p>
        <p className="font-dmsans text-sm text-ink-600 mt-1 max-w-md mx-auto">
          Cuando registres pensamientos aparecerán aquí para ayudarte a observar tu evolución.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="reflections-list">
      {items.map((r) => (
        <ReflectionCard key={r.id} reflection={r} />
      ))}
    </div>
  );
}

function ReflectionCard({ reflection }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = reflection.text.length > PREVIEW_CHARS;
  const display =
    !expanded && isLong
      ? `${reflection.text.slice(0, PREVIEW_CHARS).trimEnd()}…`
      : reflection.text;

  return (
    <article
      className="relative rounded-3xl p-5 border border-cream-100/70 backdrop-blur-md transition hover:shadow-[0_18px_50px_-26px_rgba(83,109,83,0.25)]"
      style={{
        background:
          "linear-gradient(155deg, rgba(252, 246, 232, 0.85) 0%, rgba(238, 243, 238, 0.72) 100%)",
      }}
      data-testid={`reflection-${reflection.id}`}
    >
      {/* Decorative quote mark */}
      <span
        aria-hidden="true"
        className="absolute top-3 right-4 font-outfit text-5xl leading-none text-sage-500/15 select-none"
      >
        “
      </span>

      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-2xl bg-sage-100/70 text-sage-700 flex items-center justify-center">
          <Feather size={14} />
        </div>
        <div className="leading-tight">
          <p className="tk-label">{daysAgoLabel(reflection.date)}</p>
          <p className="font-dmsans text-[11px] text-ink-600 -mt-0.5">{shortDate(reflection.date)}</p>
        </div>
      </div>

      <p
        className="font-dmsans text-[15px] text-ink-900 leading-relaxed mt-3 whitespace-pre-line"
        data-testid={`reflection-${reflection.id}-text`}
      >
        {display}
      </p>

      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-dmsans text-sage-600 hover:text-sage-700"
          data-testid={`reflection-${reflection.id}-toggle`}
        >
          {expanded ? (
            <>
              Ver menos <ChevronUp size={14} />
            </>
          ) : (
            <>
              Ver completo <ChevronDown size={14} />
            </>
          )}
        </button>
      )}

      {reflection.related?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-sand-200/40">
          <div className="flex items-start gap-1.5 flex-wrap">
            <Link2 size={11} className="text-ink-600 mt-1 shrink-0" />
            <p className="tk-label text-[10px] mt-0.5 mr-1">Relacionada con</p>
            <div className="flex flex-wrap gap-1.5">
              {reflection.related.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-sage-100/70 text-sage-700 text-[10px] font-dmsans"
                  data-testid={`reflection-${reflection.id}-related-${r.id}`}
                >
                  {r.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
